import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Target, Check, Sparkles } from "lucide-react";
import { Card, Eyebrow, StatusBadge, Btn, BtnLink } from "@/components/kit";
import { MISSIONS, CONTENT } from "@/modules/premiere-video/content";
import { useProgress } from "@/modules/premiere-video/store";
import { missionStatus } from "@/modules/premiere-video/logic";

function Stepper({ current }) {
  const { state } = useProgress();
  return (
    <div className="mb-8 flex flex-wrap items-center gap-2" data-testid="mission-stepper">
      {MISSIONS.map((m) => {
        const st = missionStatus(state, m.id);
        const active = m.id === current;
        return (
          <Link key={m.id} to={`/mission/${m.id}`} aria-label={`Mission ${m.num}`} data-testid={`stepper-mission-${m.id}`}
            className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold transition-colors ${active ? "bg-bx text-white" : st === "done" ? "bg-bx/10 text-bx" : "border border-line bg-white text-soft hover:border-gold"}`}>
            {st === "done" && !active ? <Check className="h-4 w-4" strokeWidth={2.5} /> : m.num}
          </Link>
        );
      })}
    </div>
  );
}

function Block({ label, title, children }) {
  return (
    <section className="space-y-4">
      <Eyebrow>{label}</Eyebrow>
      {title && <h2 className="text-2xl font-medium text-bx sm:text-3xl">{title}</h2>}
      {children}
    </section>
  );
}

function Lesson({ s }) {
  return (
    <Card>
      <h3 className="text-xl font-medium text-ink">{s.title}</h3>
      {s.text?.map((p, i) => <p key={i} className="mt-3 leading-relaxed text-ink/90">{p}</p>)}
      {s.list && (
        <ul className="mt-4 space-y-3">
          {s.list.map((li) => (
            <li key={li.t} className="flex gap-3">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" aria-hidden />
              <p className="leading-relaxed"><span className="font-semibold text-ink">{li.t}. </span><span className="text-soft">{li.d}</span></p>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Validation({ id, done, validate }) {
  const c = CONTENT[id];
  if (done)
    return (
      <div className="animate-fade-up rounded-[20px] border border-gold/40 bg-gradient-to-br from-gold-soft/70 to-white p-6 sm:p-7" data-testid={`mission-${id}-success`}>
        <div className="flex items-start gap-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-bx text-white"><Sparkles className="h-5 w-5" strokeWidth={1.6} /></span>
          <div>
            <p className="font-display text-xl text-bx">Mission {MISSIONS[id - 1].num} terminée</p>
            <p className="mt-1 leading-relaxed text-ink/80">{c.success}</p>
          </div>
        </div>
      </div>
    );
  if (!validate) return null;
  return (
    <Card>
      {validate.missing.length > 0 ? (
        <>
          <p className="font-medium text-ink">Pour valider cette mission, il te reste :</p>
          <ul className="mt-3 space-y-1.5 text-sm text-soft" data-testid={`mission-${id}-missing`}>
            {validate.missing.map((m) => <li key={m} className="flex gap-2"><span className="text-gold">—</span>{m}</li>)}
          </ul>
        </>
      ) : (
        <p className="font-medium text-ink">Tout est prêt. Tu peux valider ta mission.</p>
      )}
      <Btn className="mt-5 w-full sm:w-auto" disabled={validate.missing.length > 0} onClick={validate.onClick} data-testid={`mission-${id}-validate-button`}>
        <Check className="h-4 w-4" />{validate.label || "Valider ma mission"}
      </Btn>
    </Card>
  );
}

export default function MissionShell({ id, done, validate, children, after }) {
  const { state } = useProgress();
  const m = MISSIONS[id - 1];
  const c = CONTENT[id];
  const next = MISSIONS[id];
  const prev = MISSIONS[id - 2];
  return (
    <article data-testid={`mission-${id}-page`}>
      <Link to="/parcours" className="mb-6 inline-flex items-center gap-2 text-sm text-soft hover:text-bx" data-testid="back-to-dashboard"><ArrowLeft className="h-4 w-4" />Tableau de bord</Link>
      <Stepper current={id} />
      <header className="animate-fade-up">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-display text-sm font-semibold uppercase tracking-[0.2em] text-[#9A7A45]">Mission {m.num} / 07</span>
          <StatusBadge status={missionStatus(state, id)} testId={`mission-${id}-status`} />
        </div>
        <h1 className="mt-3 text-3xl font-semibold leading-tight tracking-tight text-bx sm:text-4xl lg:text-5xl">{m.title}</h1>
        <div className="mt-6 flex gap-4 rounded-[18px] border-l-2 border-gold bg-white px-5 py-4">
          <Target className="mt-0.5 h-5 w-5 shrink-0 text-bx" strokeWidth={1.5} />
          <p><span className="font-semibold text-ink">Objectif : </span><span className="text-ink/80">{c.objective}</span></p>
        </div>
      </header>
      <div className="mt-12 space-y-14">
        <Block label="Comprendre">
          <div className="space-y-4">{c.sections.map((s) => <Lesson key={s.title} s={s} />)}</div>
        </Block>
        <Block label="Exemple concret">
          <Card className="bg-[#FDFBF8]">
            <h3 className="text-xl font-medium italic text-bx">{c.example.title}</h3>
            <dl className="mt-4 space-y-3">
              {c.example.items.map((it) => (
                <div key={it.t} className="grid gap-1 sm:grid-cols-[170px_1fr] sm:gap-4">
                  <dt className="text-sm font-semibold text-bx-2">{it.t}</dt>
                  <dd className="leading-relaxed text-ink/85">{it.d}</dd>
                </div>
              ))}
            </dl>
          </Card>
        </Block>
        <Block label="À toi de jouer" title="Exercice">{children}</Block>
        <Block label="Validation"><Validation id={id} done={done} validate={validate} /></Block>
        {after}
        <nav className="flex flex-col-reverse gap-3 border-t border-line pt-8 sm:flex-row sm:justify-between" aria-label="Navigation entre missions">
          {prev ? <BtnLink to={`/mission/${prev.id}`} variant="ghost" data-testid="prev-mission-button"><ArrowLeft className="h-4 w-4" />Mission {prev.num}</BtnLink> : <span />}
          {next ? (
            <BtnLink to={`/mission/${next.id}`} variant={done ? "primary" : "outline"} data-testid="next-mission-button">Mission suivante<ArrowRight className="h-4 w-4" /></BtnLink>
          ) : (
            <BtnLink to="/resultats" variant={done ? "primary" : "outline"} data-testid="next-mission-button">Mes résultats<ArrowRight className="h-4 w-4" /></BtnLink>
          )}
        </nav>
      </div>
    </article>
  );
}
